import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiStar, FiZap, FiTarget, FiCheck } = FiIcons;

const Hero = () => {
  const [isHovered, setIsHovered] = useState(false);

  const scrollToEarlyAccess = () => {
    document.getElementById('early-access')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Chaos items animation
  const chaosItems = [
    { text: "40 tabs open", delay: 0.1 },
    { text: "3 projects", delay: 0.2 },
    { text: "Endless ideas", delay: 0.3 },
    { text: "Deadline looming", delay: 0.4 },
    { text: "Brilliant idea!", delay: 0.5 }
  ];

  // Focus items for clarity
  const focusItems = [
    { text: "Write intro", done: true },
    { text: "Review deck", done: true },
    { text: "Call investor", done: false }
  ];

  return (
    <section className="hero">
      <div className="container mx-auto px-6 py-20">
        <div className="hero-content">
          <motion.div className="hero-text"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="hero-badge">
              <SafeIcon icon={FiStar} />
              <span>Built for brains that think different</span>
            </div>

            <h1 className="hero-title">
              Brain dump.<br />
              <span className="text-teal">We organize.</span><br />
              You focus.<br />
              <span className="text-gold">Your chaos deserves clarity.</span>
            </h1>

            <p className="hero-description">
              Transform scattered ideas into focused, achievable progress—without shame,
              judgment, or burnout. Finally, a productivity tool built by someone who thinks like you do.
            </p>

            <div className="hero-cta">
              <motion.button className="btn-primary"
                onClick={scrollToEarlyAccess}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Early Access
                <SafeIcon icon={FiArrowRight} />
              </motion.button>
              <p className="cta-note">
                No spam. Just updates when we launch.
              </p>
            </div>
          </motion.div>

          <motion.div className="hero-visual"
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="visual-container">
              {/* Chaos Section */}
              <div
                className="chaos-section"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div className="chaos-header">
                  <SafeIcon icon={FiZap} className="chaos-icon" />
                  <span className="chaos-label">Brain Chaos</span>
                </div>

                <div className="chaos-bubbles">
                  {chaosItems.map((item, index) => (
                    <motion.div
                      key={index}
                      className="chaos-bubble"
                      initial={{ 
                        scale: 0,
                        opacity: 0
                      }}
                      animate={{
                        scale: 1,
                        opacity: 1,
                        rotate: isHovered ? [0, 5, -5, 0] : 0,
                        y: isHovered ? [0, -5, 0] : 0
                      }}
                      transition={{ 
                        duration: 0.5,
                        delay: item.delay,
                        rotate: {
                          duration: 0.8,
                          repeat: isHovered ? Infinity : 0,
                          repeatDelay: 2
                        },
                        y: {
                          duration: 1,
                          repeat: isHovered ? Infinity : 0,
                          repeatDelay: 1.5
                        }
                      }}
                    >
                      {item.text}
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Arrow Animation */}
              <motion.div 
                className="transform-arrow"
                animate={{ 
                  opacity: isHovered ? 1 : 0.3,
                  scale: isHovered ? [1, 1.1, 1] : 1
                }}
                transition={{ 
                  duration: 0.5,
                  scale: { repeat: isHovered ? Infinity : 0, repeatDelay: 2 }
                }}
              >
                <SafeIcon icon={FiArrowRight} className="arrow-icon" />
                <span className="arrow-text">Transforms to</span>
              </motion.div>

              {/* Clarity Section */}
              <div className="clarity-section">
                <div className="clarity-header">
                  <SafeIcon icon={FiTarget} className="clarity-icon" />
                  <span className="clarity-label">Clear Focus</span>
                </div>

                <div className="focus-list">
                  {focusItems.map((item, index) => (
                    <motion.div
                      key={index}
                      className="focus-item"
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.2 }}
                      whileHover={{ 
                        x: 5,
                        backgroundColor: "var(--color-gray-100)",
                        transition: { duration: 0.2 }
                      }}
                    >
                      <div className="focus-checkbox">
                        {item.done && <SafeIcon icon={FiCheck} />}
                      </div>
                      <span className="focus-text">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;